import { uniq } from "lodash";
import moment from "moment";
import { routeList } from "./dropList";

export const getDateWithFormat = (date) => {
  const day = date.split("-").shift();
  const month = date.substring(3, 5);
  const year = date.split("-").pop();
  const monStr = moment(new Date(`${month}/${day}/${year}`)).format("MMM");
  return `${monStr} ${day}, ${year}`;
};

export const getTimeDiff = (t2, t1) => {
  const tdiff_in_ms = t2 - t1;
  const tdiff_in_sec = tdiff_in_ms / 1000;
  const tdiff_in_min = tdiff_in_sec / 60;
  const tdiff_in_hours = tdiff_in_min / 60;
  return {
    hh: tdiff_in_hours,
    mm: tdiff_in_min,
  };
};

export const getExpiryTime = (expiryTime) => {
  const currentTime = new Date();
  const expiryTimeInMs = expiryTime.getTime();
  expiryTime.setTime(expiryTimeInMs);
  const tdiff = getTimeDiff(expiryTime.getTime(), currentTime.getTime());
  const expiresIn = {
    hh: Math.floor(tdiff.hh),
    mm: Math.floor(tdiff.mm % 60),
    ss: 0,
  };
  if (expiresIn.mm <= 0) return "Expired";
  return `${expiresIn.hh}h ${expiresIn.mm}m`;
};

export const disableBtn = (date, minutes = 15) => {
  const today = new Date();
  const dbDate = new Date(date);
  if (dbDate.getTime() < today.getTime()) return false;
  if (dbDate.getTime() > today.getTime()) {
    if (
      dbDate.getFullYear() === today.getFullYear() &&
      dbDate.getMonth() === today.getMonth() &&
      dbDate.getDate() === today.getDate()
    ) {
      if (dbDate.getHours() >= today.getHours()) {
        const minDiff = Math.floor(Math.abs(dbDate - today) / 1000 / 60);
        if (minDiff <= minutes) return false;
        return true;
      }
      return true;
    }
    return true;
  }
  return false;
};

export const getDateList = (numberOfDays = 7) => {
  const today = new Date();
  return Array.from({ length: numberOfDays }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return date.toISOString().split("T")[0];
  });
};

export function createTimeSlots(startTime, endTime, duration) {
  // Helper function to pad single digit numbers with a leading zero
  function pad(num) {
    return num < 10 ? "0" + num : num;
  }

  // Helper function to format a Date object to HH:mm string
  function formatTime(date) {
    return pad(date.getHours()) + ":" + pad(date.getMinutes());
  }

  // Parse the start and end times
  const startParts = startTime?.split(":")?.map(Number);
  const endParts = endTime?.split(":")?.map(Number);

  // Create Date objects for the start and end times
  const start = new Date();
  start.setHours(startParts[0], startParts[1], 0, 0);

  const end = new Date();
  end.setHours(endParts[0], endParts[1], 0, 0);

  // Calculate the duration in milliseconds
  const durationMs = duration * 60 * 1000;

  // Generate the time slots
  const slots = [];
  let current = new Date(start);
  while (current <= end) {
    slots.push(formatTime(current));
    current = new Date(current.getTime() + durationMs);
  }

  return slots;
}

export function isTimeBetween(time, startTime) {
  if (!time || !startTime) return false;
  const [startHour, startMinute] = startTime?.split(":").map(Number);
  const [hour, minute] = time?.split(":").map(Number);

  const start = startHour * 60 + startMinute;
  const end = startHour * 60 + 59;
  const date = hour * 60 + minute;

  if (
    (start <= date && date <= end) ||
    date === start ||
    (startHour === hour && startMinute === minute) ||
    time === startTime
  ) {
    return true;
  }
  return false;
}

export const getBorderColor = (status) => {
  switch (status) {
    case "paid" || "Paid":
      return "#80AAD3";
    case "unpaid" || "UnPaid" || "Unpaid":
      return "#ff754a";
    default:
      return "transparent";
  }
};

export function splitTimesByInterval(
  startDate,
  startTime,
  endDate,
  endTime,
  numIntervals
) {
  // Ensure date strings are in YYYY-MM-DD format
  const startDateFormatted = new Date(`${startDate}T${startTime}:00`);
  const endDateFormatted = new Date(`${endDate}T${endTime}:00`);

  // Calculate the total duration in milliseconds
  const totalDuration =
    endDateFormatted.getTime() - startDateFormatted.getTime();

  // Check for invalid input (negative duration)
  if (totalDuration <= 0) {
    throw new Error(
      "Invalid input: Start date/time must be before end date/time"
    );
  }

  // Calculate the interval length in milliseconds, considering potential remainder
  const intervalLength = Math.floor(totalDuration / numIntervals);
  const remainder = totalDuration % numIntervals;

  const intervals = [];

  // Loop through intervals, distributing remainder evenly
  for (let i = 0; i < numIntervals; i++) {
    const startTime = new Date(
      startDateFormatted.getTime() +
        i * intervalLength +
        Math.floor((remainder * i) / numIntervals)
    );
    const endTime = new Date(
      startTime.getTime() +
        intervalLength +
        (i === numIntervals - 1 ? remainder % numIntervals : 0)
    );

    intervals.push({
      startDate: moment(new Date(startTime)).format("YYYY-MM-DD"),
      startTime: moment(new Date(startTime)).format("HH:mm"),
      endDate: moment(new Date(endTime)).format("YYYY-MM-DD"),
      endTime: moment(new Date(endTime)).format("HH:mm"),
    });
  }

  return intervals;
}

export const getTimeFormat = (time) => {
  if (/^\d{2}:\d{2}$/.test(time)) {
    return time;
  }
  return moment(new Date(time)).format("HH:mm");
};

export const getDateFormat = (date) => {
  return moment(new Date(date)).format("YYYY-MM-DD");
};

export function getWeekStartEndDate() {
  const today = new Date();

  // Get the day of the week (0: Sunday, 1: Monday, ..., 6: Saturday)
  const dayOfWeek = today.getDay();

  // Calculate the start of the week (Sunday)
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - dayOfWeek);

  // Calculate the end of the week (Saturday)
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return { startDate: weekStart, endDate: weekEnd };
}

export function getUpcoming7Dates() {
  const dates = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    dates.push(date);
  }
  return dates;
}

function getSlots(startDateISO, endDateISO, durationHours) {
  const slots = [];
  const durationMilliseconds = durationHours * 60 * 60 * 1000;
  let currentDate = new Date(startDateISO);

  const endDate = new Date(endDateISO);

  while (currentDate < endDate) {
    const nextSlot = new Date(currentDate.getTime() + durationMilliseconds);

    if (nextSlot > endDate) {
      break;
    }

    slots.push({
      startDate: getDateFormat(currentDate),
      startTime: getTimeFormat(currentDate),
      endDate: getDateFormat(nextSlot),
      endTime: getTimeFormat(nextSlot),
    });

    currentDate = nextSlot;
  }

  return slots;
}

export function getDateAndTimeList(
  strtDate,
  enDate,
  startTime,
  endTime,
  duration //duration in hours
) {
  const [hr, min] = getTimeFormat(startTime)?.split(":");
  const [enHr, enMin] = getTimeFormat(endTime)?.split(":");
  const startDate = new Date(
    new Date(getDateFormat(strtDate)).setHours(hr, min)
  );
  const endDate = new Date(
    new Date(getDateFormat(enDate)).setHours(
      Number(enHr) + Number(duration),
      enMin
    )
  );

  let reqDates = [];

  while (startDate <= endDate) {
    const reqEndTime = new Date(
      new Date(getDateFormat(startDate)).setHours(
        Number(enHr) + Number(duration),
        enMin
      )
    );
    const times = getSlots(
      startDate.toISOString(),
      reqEndTime.toISOString(),
      duration
    );
    reqDates = [...reqDates, ...times];
    startDate.setDate(startDate.getDate() + 1);
  }
  return reqDates;
}

export function validateTimeSlots(clinicHourList, duartion) {
  return clinicHourList?.every((day) => {
    return day?.slots?.every((slot) => {
      const startTime = new Date(slot?.startTime);
      const endTime = new Date(slot?.endTime);
      const timeDiff = getTimeDiff(endTime, startTime);
      return endTime > startTime && timeDiff?.mm >= Number(duartion);
    });
  });
}

export const getAllDatesFromArray = (list) => {
  const dates = [];
  list?.forEach((li) => {
    const startDate = new Date(li?.date);
    const endDate = new Date(li?.endDate);

    for (const d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
  });
  return uniq(dates);
};

export const getUpcomingSlotsWithoutBook = (slots) => {
  const currTime = moment(new Date()).format("HH:mm");
  return slots?.filter((item) => item?.time > currTime && item?.book !== "Y");
};

export const getTodayWithYMDFormat = () => {
  return moment(new Date()).format("YYYY-MM-DD");
};

export const getKey = () => {
  return (Math.random() + 1).toString(36).substring(7);
};

export function getHourRanges(times) {
  if (!times || times?.length === 0) return [];
  const uniqueHours = new Set(
    times?.map((time) => {
      const hour = time?.split(":")[0].padStart(2, "0");
      return `${hour}:00-${hour}:59`;
    })
  );

  return Array.from(uniqueHours);
}

export function normalizeDate(dateStr) {
  if (!dateStr) return "";

  // If already in yyyy-mm-dd format, return as is
  const yyyymmddPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (yyyymmddPattern.test(dateStr)) {
    return dateStr;
  }

  // Detect delimiter
  const delimiter = dateStr.includes("-")
    ? "-"
    : dateStr.includes("/")
    ? "/"
    : null;
  if (!delimiter) return ""; // unsupported format

  const [day, month, yearPart] = dateStr.split(delimiter);
  const year = yearPart.length === 4 ? yearPart : `20${yearPart}`;

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

export const reduceYearsAndMonthsToStartOfMonth = ({
  years = 0,
  months = 0,
}) => {
  const currentDate = new Date();

  // Calculate the total reduction in terms of months
  const totalMonths = Math.floor(years * 12) + Math.floor(months);
  const remainingDays =
    (years * 12 - Math.floor(years * 12) + (months - Math.floor(months))) *
    30.44;

  const pastDate = new Date(currentDate);
  pastDate.setMonth(pastDate.getMonth() - totalMonths);
  pastDate.setDate(pastDate.getDate() - remainingDays);

  const startOfMonthDate = new Date(
    pastDate.getFullYear(),
    pastDate.getMonth(),
    1
  );
  return moment(startOfMonthDate).format("YYYY-MM-DD");
};

export const getTreatmentRecordBasedType = (list) => {
  return list
    ?.filter(
      (det) =>
        (det?.billType === "administered" ||
          det?.type === "consultation" ||
          det?.name === "Hospitalization") &&
        det?.quantity > 0
    )
    .sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));
};

export const getPharmacyRecordBasedType = (list) => {
  return list
    ?.filter(
      (det) =>
        det?.billType !== "administered" &&
        det?.type !== "consultation" &&
        det?.name !== "Hospitalization" &&
        det?.quantity > 0
    )
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
};

export const getTaxAndSubTotalAndTotal = (list) => {
  let tax = 0,
    subTotal = 0,
    discount = 0,
    adminCharge = 0;
  tax = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.tax) ?? 0);
  }, 0);
  subTotal = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.total) ?? 0);
  }, 0);
  discount = list?.reduce((acc, obj) => {
    return acc + (Number(obj?.discount) ?? 0) * (Number(obj?.quantity) ?? 0);
  }, 0);
  adminCharge = list?.reduce((acc, obj) => {
    return (
      acc +
      (obj?.adminCharge === "Y"
        ? Number(obj?.adminChargeAmount) * (Number(obj?.quantity) ?? 0)
        : 0)
    );
  }, 0);
  return { tax, subTotal, total: subTotal + tax, discount, adminCharge };
};

export const getBillingAmount = (list) => {
  const treatRecords = getTreatmentRecordBasedType(list);
  const pharRecords = getPharmacyRecordBasedType(list);
  let subTotal = 0,
    tax = 0;
  if (treatRecords?.length > 0) {
    subTotal =
      subTotal +
      treatRecords?.reduce((acc, obj) => {
        return (
          acc + (Number(obj?.total ?? 0) + Number(obj?.adminChargeAmount ?? 0))
        );
      }, 0);
  }
  if (pharRecords?.length > 0) {
    subTotal =
      subTotal +
      pharRecords?.reduce((acc, obj) => {
        return acc + Number(obj?.price ?? 0);
      }, 0);
    tax =
      tax + pharRecords?.reduce((acc, obj) => acc + Number(obj?.tax ?? 0), 0);
  }

  return {
    subTotal,
    tax,
    total: subTotal + tax,
  };
};

export const convertStringToObject = (string) => {
  const obj = {};
  string.split("\n").forEach((line) => {
    const [key, value] = line.split(":").map((part) => part.trim());
    obj[key.charAt(0).toLowerCase() + key.slice(1)] =
      value === "Null" ? null : value;
  });
  return obj;
};

export const getExactNumber = (str) => {
  if (typeof str === "number") return str;
  let match = str?.match(/\d+(\.\d+)?/);
  return match ? match[0] : 0;
};

const getPercentageAsValue = (discount = 0, unitRate = 0) => {
  return (Number(unitRate) / 100) * Number(discount);
};

export const getTaxableValue = ({
  quantity = 1,
  unitRate = 0,
  discount = 0,
}) => {
  return Number(
    Number(quantity) *
      (Number(unitRate ?? 0) - Number(getPercentageAsValue(discount, unitRate)))
  );
};

export const getTaxValue = ({ taxableValue = 1, cgst = 0, sgst = 0 }) => {
  return Number((Number(taxableValue) / 100) * (Number(cgst) + Number(sgst)));
};

export const getTotalValues = ({ list = [], additionalCharges = 0 }) => {
  let taxableValues = 0;
  let taxes = 0;

  if (list?.length > 0) {
    taxableValues = list?.reduce(
      (acc, obj) => acc + Number(obj?.taxableValue),
      0
    );
    taxes = list?.reduce((acc, obj) => acc + Number(obj?.taxes), 0);
  }

  return {
    taxableValues,
    taxes,
    grandTotal: taxableValues + taxes + Number(additionalCharges ?? 0),
  };
};

export const datePlusOneDay = new Date(
  new Date().setDate(new Date().getDate() + 1)
);

export const getTypeByAppointmentType = (typ) => {
  return typ === "scheduled"
    ? "Upcoming"
    : typ === "inprogress"
    ? "Checked-in"
    : typ === "intransit"
    ? "In Transit"
    : typ === "consultation"
    ? "Consultation"
    : typ === "finalize"
    ? "Billing"
    : typ === "completed"
    ? "Completed"
    : typ === "Pending"
    ? "Pending"
    : typ?.toLowerCase() === "canceled" || typ?.toLowerCase() === "cancelled"
    ? "Cancelled"
    : "";
};

export function getDatesInRange(startDate, endDate) {
  const dateList = [];
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const day = ("0" + currentDate.getDate()).slice(-2);
    const month = ("0" + (currentDate.getMonth() + 1)).slice(-2);
    const year = currentDate.getFullYear();
    const formattedDate = new Date(`${year}-${month}-${day}`);
    dateList.push(formattedDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateList;
}

export const getStartAndEndDatesOfWeekByType = ({
  key = "next",
  weeks = 1,
}) => {
  if (key === "next") {
    const startDate = new Date();
    const endDate = new Date(new Date().setDate(new Date().getDate() + 6));
    startDate.setDate(new Date(startDate).getDate() + weeks * 7);
    endDate.setDate(new Date(endDate).getDate() + weeks * 7);
    return { startDate, endDate };
  }
  if (key === "prev") {
    // Calculate the end date (yesterday)
    let endDate = new Date();
    endDate.setDate(endDate.getDate() - 1); // Subtract 1 day to get yesterday's date

    // Get the exact start and end dates based on the weekNumber
    if (weeks > 1) {
      const daysToSubtract = (weeks - 1) * 7;
      endDate.setDate(endDate.getDate() - daysToSubtract);
    }

    // Calculate the start date (endDate - 6 days)
    let startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6); // Subtract 6 days from endDate

    return { startDate, endDate };
  }
};

export const getPriceWithoutTax = ({
  unitSellingPrice = 0,
  cgst = 0,
  sgst = 0,
}) => {
  const onePerPrice = Number(unitSellingPrice ?? 0) / 100;
  const tax =
    Number(onePerPrice ?? 0) * Number(Number(cgst ?? 0) + Number(sgst ?? 0));
  return {
    price: Number(Number(unitSellingPrice ?? 0) - Number(tax)) ?? 0,
    tax,
  };
};

export const getTotalEstimatedCost = (list = [], serCost = 0) => {
  if (list?.length === 0 || !list) return serCost;
  return (
    list?.reduce(
      (acc, obj) =>
        acc + (obj?.cost ?? obj?.estimatedCost ?? obj?.estCost ?? 0),
      0
    ) + Number(serCost)
  );
};

export function splitTimeRange(startTime, endTime, slotDuration) {
  let slots = [];
  let current = new Date(`1970-01-01T${startTime}:00`);
  let endDate = new Date(`1970-01-01T${endTime}:00`);

  while (current < endDate) {
    let slotEnd = new Date(current.getTime() + slotDuration * 60000);
    if (slotEnd > endDate) {
      break; // Exit the loop if the slot exceeds the end time
    }

    slots.push({
      startTime: moment(current).format("HH:mm"),
      endTime: moment(slotEnd).format("HH:mm"),
    });

    current = slotEnd;
  }

  return slots;
}

export function splitMultipleRanges(ranges = [], slotDuration, dateStr) {
  if (ranges?.length === 0 || !ranges) return [];
  let allSlots = [];
  if (dateStr === moment().format("YYYY-MM-DD")) {
    let now = moment();
    ranges.forEach((range) => {
      let rangeStart = moment(`1970-01-01T${range?.startTime}:00`);
      let rangeEnd = moment(`1970-01-01T${range?.endTime}:00`);

      // Adjust the start time if it's in the past
      if (rangeStart?.isBefore(now)) {
        rangeStart = now
          .clone()
          .add(slotDuration - (now.minute() % slotDuration), "minutes")
          .startOf("minute");
      }

      let slots = splitTimeRange(
        rangeStart?.format("HH:mm"),
        rangeEnd?.format("HH:mm"),
        slotDuration
      );
      allSlots = allSlots.concat(slots);
    });
  } else {
    for (let range of ranges) {
      let slots = splitTimeRange(range.startTime, range.endTime, slotDuration);
      allSlots = allSlots.concat(slots);
    }
  }
  return allSlots;
}

function removeUnavailableSlots(slots, ...unavailableArrays) {
  return slots?.filter((slot) => {
    return !unavailableArrays?.some((unavailableSlots) =>
      unavailableSlots.some((unavailable) => {
        const startSlot = new Date(`1970-01-01T${slot.startTime}:00`);
        const endSlot = new Date(`1970-01-01T${slot.endTime}:00`);
        const startUnavailable = new Date(
          `1970-01-01T${unavailable.startTime}`
        );
        const endUnavailable = new Date(`1970-01-01T${unavailable.endTime}`);

        return (
          (startSlot >= startUnavailable && startSlot < endUnavailable) ||
          (endSlot > startUnavailable && endSlot <= endUnavailable) ||
          (startSlot <= startUnavailable && endSlot >= endUnavailable)
        );
      })
    );
  });
}

const getReqBookedAppointments = (list) => {
  return list
    ?.filter(
      (li) =>
        li?.appoinmentStartTime !== null && li?.appointmentEndTime !== null
    )
    ?.map((li) => ({
      startTime: li?.appoinmentStartTime,
      endTime: li?.appointmentEndTime,
    }));
};

export function removeRepeatingTimes(slots) {
  const uniqueSlots = [];
  const slotSet = new Set();

  slots.forEach((slot) => {
    const key = `${slot.startTime}-${slot.endTime}`;
    if (!slotSet.has(key)) {
      slotSet.add(key);
      uniqueSlots.push(slot);
    }
  });

  return uniqueSlots;
}

export function filterHolidaysByDate(holidays, givenDate) {
  return holidays?.filter(
    (holiday) =>
      new Date(holiday.date) <= new Date(givenDate) &&
      new Date(holiday.endDate) >= new Date(givenDate)
  );
}

export const getRequiredSlotList = ({
  availability,
  date,
  isDuration = false,
  bookedApnts = [],
  holidays = [],
}) => {
  const day = moment(new Date(date)).format("dddd");
  const currDate = moment(new Date(date))?.format("YYYY-MM-DD");
  const holidaysOnly = holidays?.filter((hl) => hl?.type === "holliday");
  if (filterHolidaysByDate(holidaysOnly, currDate)?.length) return [];
  const unAvailTimesOfTheDay = holidays?.filter(
    (hl) => hl?.type !== "holliday" && hl?.date === currDate
  );
  const matchedDay = availability?.find(
    (avail) => avail?.day?.toLowerCase() === day?.toLowerCase()
  );
  if (isDuration) return matchedDay?.duration;
  //getting all slots from times Array
  const slotList = splitMultipleRanges(matchedDay?.times, 15, currDate);
  const selectedDateSlots = removeRepeatingTimes(slotList);
  const availableSlotsAfterBooking = removeUnavailableSlots(
    selectedDateSlots ?? [],
    getReqBookedAppointments(bookedApnts) ?? [],
    unAvailTimesOfTheDay ?? []
  );
  return availableSlotsAfterBooking ?? [];
};

export function getMonthsDifference(dateString) {
  const today = new Date();
  const givenDate = new Date(dateString);
  // Calculate the difference in years and months
  const yearDifference = today?.getFullYear() - givenDate?.getFullYear();
  const monthDifference = today?.getMonth() - givenDate?.getMonth();
  // Total months difference
  const totalMonths = yearDifference * 12 + monthDifference;
  return totalMonths;
}

export function adjustTime(inputTime, minutes) {
  const [hours, mins] = inputTime?.split(":").map(Number);
  let time = new Date();
  time?.setHours(hours);
  time?.setMinutes(mins);
  // Adjust time by the specified minutes
  time?.setMinutes(time?.getMinutes() + minutes);
  return time?.toTimeString().slice(0, 5); // Return in HH:MM format
}

export function getAllSelectedSlots(array1, array2) {
  // Sort array2 based on startTime
  array2.sort(
    (a, b) =>
      new Date(`1970-01-01T${a.startTime}:00`) -
      new Date(`1970-01-01T${b.startTime}:00`)
  );

  // Get the range from the sorted array2
  const start = new Date(`1970-01-01T${array2[0].startTime}:00`);
  const end = new Date(`1970-01-01T${array2[array2.length - 1].endTime}:00`);

  // Filter array1 based on the range
  return array1.filter((slot) => {
    const slotStart = new Date(`1970-01-01T${slot.startTime}:00`);
    return slotStart >= start && slotStart < end;
  });
}

export const getDateDiffInDays = (
  startDate = new Date(),
  endDate = new Date()
) => {
  const date1 = new Date(startDate);
  const date2 = new Date(endDate);

  // Calculate the difference in milliseconds
  const differenceInMs = date2.getTime() - date1.getTime();

  // Convert milliseconds to days
  const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);
  return Math.ceil(differenceInDays);
};

const dayMap = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const getUpcoming60Dates = (daysArray = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Ensure time consistency
  if (daysArray?.length === 0 || !daysArray) return [];
  const excludedDays = daysArray
    ?.filter((d) => !d.status)
    ?.map((d) => dayMap[d.day]);
  let upcomingDates = [];
  let currentDate = new Date(today);
  let endDate = new Date(today);
  endDate.setMonth(today.getMonth() + 2); // Limit to two months
  while (currentDate <= endDate) {
    currentDate.setDate(currentDate.getDate() + 1);
    currentDate.setHours(0, 0, 0, 0); // Reset time for consistency
    if (excludedDays.includes(currentDate.getDay())) {
      upcomingDates.push(moment(currentDate).format("YYYY-MM-DD")); // Format YYYY-MM-DD
    }
  }
  return upcomingDates;
};

export const getSelectedInvoicesBalanceDue = (list = [], advanceAmount = 0) => {
  let total = 0;
  total = list?.reduce((acc, bill) => {
    return (
      acc +
      Number(
        (Number(
          getTaxAndSubTotalAndTotal(
            getTreatmentRecordBasedType(bill?.paymentDetails)
          )?.subTotal +
            getTaxAndSubTotalAndTotal(
              getPharmacyRecordBasedType(bill?.paymentDetails)
            )?.subTotal
        ) ?? 0) - Number(bill?.amtPaid ?? 0)
      )
    );
  }, 0);

  return (Number(total) ?? 0) - Number(advanceAmount ?? 0);
};

export const getRefundValues = (list = []) => {
  let subTotal = 0,
    tax = 0;

  subTotal = list?.reduce((acc, obj) => {
    const reqValue = Number(obj?.price) * Number(obj?.returnQuantity);
    return acc + (Number(reqValue) ?? 0);
  }, 0);
  tax = list?.reduce((acc, obj) => {
    return (
      acc +
      Number(
        (Number(
          Number(obj?.tax) /
            Number(obj?.quantity > 0 ? obj?.quantity : obj?.returnQuantity)
        ) ?? 0) * Number(obj?.returnQuantity) ?? 0
      )
    );
  }, 0);

  return {
    total: Number(
      (isNaN(Number(subTotal)) ? 0 : Number(subTotal)) +
        (isNaN(Number(tax)) ? 0 : Number(tax))
    ),
    subTotal,
    tax,
  };
};

export const getTaxValueByBatchAndTaxPer = (batch = null, qty = 1) => {
  let total = 0,
    tax = 0;
  if (!batch) return { tax, total };
  const taxPer = Number(batch?.cgst ?? 0) + Number(batch?.sgst ?? 0);
  tax = (Number(batch?.unitSellingPrice) / 100) * Number(taxPer);
  total = Number(batch?.unitSellingPrice) * Number(qty);
  return { tax, total };
};

const getReqStock = (stocks, stockId) => {
  return stocks?.data?.find((stc) => stc?.stockId === stockId);
};

const getBatchDetails = (stock, batchId = null) => {
  return stock?.stockBatch?.find((bat) => bat?.batchId === batchId);
};

export const getSupplierRefundValues = (list = [], stocks = {}) => {
  let subTotal = 0,
    taxes = 0,
    total = 0;
  list?.forEach((li) => {
    const reqStock = getReqStock(stocks, li?.stockId);
    const batc = getBatchDetails(reqStock, li?.batchNumber?.value);
    const taxPer = Number(batc?.cgst ?? 0) + Number(batc?.sgst ?? 0);
    const tax =
      (Number(batc?.unitSellingPrice ?? 0) / 100) * Number(taxPer ?? 0);
    const reqValue =
      Number(batc?.unitSellingPrice ?? 0) * Number(li?.returnQuantity ?? 0);
    const reqTax = Number(tax) * Number(li?.returnQuantity ?? 0);
    taxes = taxes + (Number(reqTax) ?? 0);
    total = total + (Number(reqValue) ?? 0);
  });
  subTotal = Number(total) - Number(taxes);
  return { subTotal, taxes, total };
};

export const getOnlyName = (str = "") => {
  const words = str?.split(" "); // Convert string to array
  const lastItem = words[words?.length - 1]; // Get the last element
  if (!isNaN(lastItem)) {
    words.pop(); // Remove last item if it's a number
  }
  return words.join(" ");
};

export const getPetSearchType = (str = "") => {
  if (str?.length === 0) return "";
  const strNumber = Number(str);
  const isNonNumber = isNaN(strNumber);
  let type = "mobile";
  if (str?.length > 0 && !isNonNumber) type = "patient";
  if (str?.length > 5 && !isNonNumber) type = "mobile";
  if (str?.length > 0 && isNonNumber) type = "name";
  return `&filter=${type}`;
};

export function getWeekOffset(clickedDateStr, startDateStr) {
  const currentWeekStart = new Date(startDateStr);
  const clickedDate = new Date(clickedDateStr);
  const msPerWeek = 7 * 24 * 60 * 60 * 1000; // Milliseconds in a week
  const offset = Math.floor((clickedDate - currentWeekStart) / msPerWeek);
  return {
    prev: offset < 0 ? Math.abs(offset) : 0,
    next: offset > 0 ? offset : 0,
  };
}

export function getWeekRange(dateString, offset = 0) {
  const inputDate = new Date(dateString);
  const dayOfWeek = inputDate.getDay(); // Get day index (0 for Sunday, 6 for Saturday)

  // Calculate start of the week (Sunday)
  const startDate = new Date(inputDate);
  startDate.setDate(inputDate.getDate() - dayOfWeek + offset * 7);

  // Calculate end of the week (Saturday)
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);

  // Format YYYY-MM-DD
  const formatDate = (date) => date.toISOString().split("T")[0];
  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
}

export function getAddedDate(date, duration = 1) {
  const reqDur = duration - 1;
  if (reqDur > 0) {
    const reqDate = new Date(date);
    return new Date(new Date(reqDate).setDate(reqDate.getDate() + reqDur));
  }
  return new Date(date);
}

export const getTotalEstCostForWorkbook = (
  procedures = [],
  consumList = [],
  adminList = [],
  diagList = []
) => {
  let total = 0;
  if (procedures?.length > 0) {
    total =
      total +
      procedures?.reduce((acc, obj) => {
        return acc + (Number(obj?.variant?.serviceCharge ?? 0) ?? 0);
      }, 0);
  }
  if (consumList?.length > 0) {
    total =
      total +
      consumList?.reduce((acc, obj) => {
        return acc + (Number(obj?.estCost ?? 0) ?? 0);
      }, 0);
  }
  if (adminList?.length > 0) {
    total =
      total +
      adminList?.reduce((acc, obj) => {
        return acc + (Number(obj?.estCost ?? 0) ?? 0);
      }, 0);
  }
  if (diagList?.length > 0) {
    total =
      total +
      diagList?.reduce((acc, obj) => {
        return acc + (Number(obj?.total ?? 0) ?? 0);
      }, 0);
  }
  return total;
};

export const extensionToMime = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  json: "application/json",
  zip: "application/zip",
  mp4: "video/mp4",
  mp3: "audio/mpeg",
};

// export async function urlToFile(url, fileName) {
//   const response = await fetch(url);
//   const blob = await response.blob();
//   const mimeType = blob.type || "application/octet-stream";

//   // Convert Blob to File (in browser-like environments)
//   const file = new File([blob], fileName, { type: mimeType });
//   return file;
// }

export async function urlToFile(url, fallbackName = "file") {
  const response = await fetch(url);
  const blob = await response.blob();

  // Try to extract filename from URL
  const urlParts = url.split("/");
  const lastPart = urlParts[urlParts.length - 1].split("?")[0];
  const nameFromUrl =
    lastPart || `${fallbackName}.${blob.type.split("/")[1] || "bin"}`;

  const mimeType = blob.type || "application/octet-stream";
  return new File([blob], nameFromUrl, { type: mimeType });
}

export function generate4DigitRandomNumber() {
  return Math.floor(1000 + Math.random() * 9000);
}

function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

// Check if two time ranges overlap
function isOverlapping(start1, end1, start2, end2) {
  return start1 < end2 && start2 < end1;
}

export const getUnAvailableGroomList = (
  facilitySlots = [],
  actualSlots = []
) => {
  const unavailableRanges = facilitySlots?.flatMap((f) =>
    f?.clinicFacilitiesUnavaility?.map((ua) => ({
      start: timeToMinutes(ua?.appointmentStartTime),
      end: timeToMinutes(ua?.appointmentEndTime),
    }))
  );

  const availableSlots = actualSlots?.filter((slot) => {
    const slotStart = timeToMinutes(slot?.startTime);
    const slotEnd = timeToMinutes(slot?.endTime);

    // Return true if this slot does NOT overlap with any unavailable range
    return !unavailableRanges.some(
      (range) => slotStart < range?.end && slotEnd > range?.start
    );
  });

  return availableSlots ?? [];
};

export const getOnlyAvailableSlots = (facilitySlots, actualSlots) => {
  const filteredSlots = actualSlots?.filter((slot) => {
    const slotStart = timeToMinutes(slot?.startTime);
    const slotEnd = timeToMinutes(slot?.endTime);

    // Check for overlap with any appointment
    const overlaps = facilitySlots?.some((appointment) => {
      const apptStart = timeToMinutes(appointment?.startTime);
      const apptEnd = timeToMinutes(appointment?.endTime);
      return isOverlapping(slotStart, slotEnd, apptStart, apptEnd);
    });

    return !overlaps;
  });
  return filteredSlots ?? [];
};

const isOverlappingForGroomer = (slotStart, slotEnd, rangeStart, rangeEnd) =>
  slotStart < rangeEnd && slotEnd > rangeStart;

const toMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export const getSlotsForGroomer = (facilities = [], actualSlots = []) => {
  const availableSlots = actualSlots?.filter(({ startTime, endTime }) => {
    const slotStart = toMinutes(startTime);
    const slotEnd = toMinutes(endTime);

    // Check all facilities
    return facilities?.some((facility) => {
      const overlaps = facility?.clinicFacilitiesUnavaility.some(
        ({ appointmentStartTime, appointmentEndTime }) =>
          isOverlappingForGroomer(
            slotStart,
            slotEnd,
            toMinutes(appointmentStartTime),
            toMinutes(appointmentEndTime)
          )
      );
      return !overlaps; // true if this facility has no conflict
    });
  });
  return availableSlots ?? [];
};

export const getAvailableBayAtTime = (facilities = [], time) => {
  const checkTime = toMinutes(time);
  let candidates = [];

  for (const bay of facilities) {
    const unavailability = bay?.clinicFacilitiesUnavaility || [];

    // Sort slots by end time descending
    const sorted = [...unavailability].sort(
      (a, b) =>
        toMinutes(b?.appointmentEndTime) - toMinutes(a?.appointmentEndTime)
    );

    let latestEndTime = null;
    let isAvailable = true;

    for (const slot of sorted) {
      const start = toMinutes(slot?.appointmentStartTime);
      const end = toMinutes(slot?.appointmentEndTime);

      if (checkTime >= start && checkTime < end) {
        isAvailable = false; // overlaps
        break;
      }

      // Track latest end time before this time
      if (end <= checkTime) {
        latestEndTime = end;
        break;
      }
    }

    if (isAvailable) {
      candidates.push({
        ...bay,
        lastEndTime: latestEndTime ?? 0,
      });
    }
  }

  if (candidates.length === 0) return null;

  // Pick the bay that was last used most recently (max utilization)
  candidates.sort((a, b) => b.lastEndTime - a.lastEndTime);
  return {
    facilityId: candidates[0].facilityId,
    name: candidates[0].name,
  };
};

export const getClassNameByServiceType = (serviceType) => {
  if (serviceType?.toLowerCase() === "consultation")
    return "card-bot-rose-back";
  if (serviceType?.toLowerCase() === "grooming") return "card-bot-blue-back";
  if (serviceType?.toLowerCase() === "lab tests") return "card-bot-green-back";
  if (serviceType?.toLowerCase() === "radiology tests")
    return "card-bot-yellow-back";
  if (serviceType?.toLowerCase() === "surgery") return "card-bot-orange-back";
  if (serviceType?.toLowerCase() === "others") return "card-bot-pink-back";
};

export const getClassNameForCalendar = (serviceType) => {
  if (serviceType?.toLowerCase() === "consultation") return "cal-purple-box";
  if (serviceType?.toLowerCase() === "grooming") return "cal-blue-box";
  if (["lab tests", "vaccination"]?.includes(serviceType?.toLowerCase()))
    return "cal-green-box";
  if (serviceType?.toLowerCase() === "radiology tests") return "cal-yellow-box";
  if (serviceType?.toLowerCase() === "surgery") return "cal-orange-box";
  if (serviceType?.toLowerCase() === "others") return "cal-pink-box";
  return "cal-purple-box";
};

const today = new Date().toISOString().split("T")[0];

const isAvailableToday = (startDate, endDate) => {
  if (!startDate && !endDate) return true;
  if (startDate && endDate) return today >= startDate && today <= endDate;
  if (startDate) return today >= startDate;
  if (endDate) return today <= endDate;
  return false;
};

export const getDiscountCodeAvailableToday = (
  discounts = [],
  userId = null,
  billAmount = 0,
  billQty = 0,
  newCustomer = false,
  hasDiscount = false,
  petType = null
) => {
  return discounts?.filter((discount) => {
    const {
      startDate,
      endDate,
      purchaseType,
      minAmount,
      minQuantity,
      billingHistory,
      specificCustomers,
      limitUsagePerCustomer,
      maxPerCustomerUses,
      limitUsageTotal,
      maxTotalUses,
      cannotBeCombined,
      discountMethod,
    } = discount;
    if (discountMethod === "Automatic discount") return false;
    if (!isAvailableToday(startDate, endDate)) return false;
    if (purchaseType === "One-time" && billingHistory?.length > 0) {
      if (billingHistory?.some((hst) => hst?.userId === userId)) return false;
    }
    if (minAmount > 0 && billAmount < minAmount) return false;
    if (minQuantity > 0 && billQty < minQuantity) return false;
    if (specificCustomers?.length > 0) {
      if (specificCustomers?.includes("newCustomers")) {
        if (!newCustomer) return false;
      }
    }
    if (limitUsagePerCustomer && billingHistory?.length > 0) {
      const listSize = billingHistory?.filter((hst) => hst?.userId === userId);
      if (listSize?.length >= maxPerCustomerUses) return false;
    }
    if (limitUsageTotal && billingHistory?.length > 0) {
      if (maxTotalUses === 0) return false;
    }
    if (cannotBeCombined && hasDiscount) return false;
    if (
      (specificCustomers?.includes("Household") ||
        specificCustomers?.includes("Rescue")) &&
      !specificCustomers?.includes(petType)
    )
      return false;
    return true;
  });
};

export const getPrevDiscountValues = (discounts = []) => {
  const result = { percentage: 0, price: 0 };
  if (discounts?.length === 0) return result;
  discounts.forEach((item) => {
    const { discountType, discountValue } = item;
    if (discountType?.toLowerCase() === "percentage") {
      result.percentage += discountValue;
    } else {
      result.price += discountValue;
    }
  });
  return result;
};

const getBatch = (
  stockId,
  batchId = null,
  retStock = false,
  clinicStocks = []
) => {
  if (!stockId) return null;
  const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
  if (retStock) return reqStock;
  if (!batchId) return null;
  return reqStock?.stockBatch?.find((bat) => bat?.batchId === batchId) ?? null;
};

const getConsumAdminListByServIdAndVarntId = (
  servId = null,
  varntId = null,
  services = [],
  clinicStocks = [],
  pharmacyTaxId = null
) => {
  const reqVarnt = services
    ?.find((ser) => ser?.serviceId === servId)
    ?.serviceVariants?.find((vnt) => vnt?.variantId === varntId);
  let reqConsums = [],
    reqAdmins = [],
    reqPres = [];
  if (reqVarnt?.serviceDetails?.length > 0) {
    reqVarnt?.serviceDetails
      ?.filter(
        (li) =>
          (li?.alterType === "N" && !li?.alterName) ||
          (li?.alterType === "Y" && !li?.alterName)
      )
      ?.forEach((li) => {
        reqConsums.push({
          ...li,
          estCost: li?.estimatedCost,
          delete: false,
          reason: null,
          isEdit: true,
          deleteable: false,
          status: null,
          detailId: null,
          serviceId: servId,
          variantId: varntId,
        });
      });
  }
  if (reqVarnt?.servicePrescreiptions?.length > 0) {
    reqVarnt?.servicePrescreiptions
      ?.filter(
        (li) =>
          li?.administered?.toLowerCase() === "administered" &&
          ((li?.alterType === "N" && !li?.alterName) ||
            (li?.alterType === "Y" && !li?.alterName))
      )
      ?.forEach((li, i) => {
        const batch = getBatch(
          li?.stockId ?? null,
          li?.batchId ?? null,
          clinicStocks
        );
        const batchNumber = {
          name: batch?.batchNo,
          value: batch?.batchId,
        };
        const admnObj = {
          ...li,
          reason: null,
          route: routeList?.find((rl) => rl?.value === li?.vaccineType),
          i,
          batchNumber,
          deleteable: false,
          delete: false,
          isEdit: true,
          estCost: li?.estimatedCost ?? 0,
          status: null,
          prescriptionId: li?.prescriptionId,
          detailId: null,
          serviceId: servId,
          variantId: varntId,
        };
        reqAdmins.push(admnObj);
      });

    reqVarnt?.servicePrescreiptions
      ?.filter((li) => li?.administered?.toLowerCase() === "prescribed")
      ?.forEach((pre) => {
        const selStock = clinicStocks?.data?.find(
          (stc) => stc?.stockId === pre?.stockId
        );
        const reqObj = {
          administered: false,
          type: pre?.type ?? null,
          stockId: pre?.stockId,
          batchId: pre?.batchId ?? null,
          name: pre?.name ?? "",
          dosage: pre?.dosage ?? "",
          duration: pre?.duration?.length > 0 ? pre?.duration : "1",
          meal: pre?.meal,
          quantity: pre?.quantity ?? 1,
          subUnit: "Unit",
          morning: pre?.morning ?? false,
          afternoon: pre?.afternoon ?? false,
          night: pre?.night ?? false,
          adminChargeAmount: 0,
          adminCharge: "N",
          percentage: false,
          delete: false,
          discontinued: "N",
        };
        const btchDet = selStock?.stockBatch?.[0];
        const taxPercent =
          pharmacyTaxId?.length > 0
            ? (btchDet?.cgst ? Number(btchDet?.cgst) : 0) +
              (btchDet?.sgst ? Number(btchDet?.sgst) : 0)
            : 0;
        const reqPercent = 100 + Number(taxPercent);
        const unitPrice =
          (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) /
          (reqPercent / 100);
        const taxValue =
          Number(
            (btchDet?.unitSellingPrice
              ? Number(btchDet?.unitSellingPrice)
              : 0) - Number(unitPrice)
          ) ?? 0;
        reqObj.price = unitPrice ?? 0;
        reqObj.mrpPrice = btchDet?.unitSellingPrice
          ? Number(btchDet?.unitSellingPrice)
          : 0;
        reqObj.mrpDiscount =
          (Number(
            btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0
          ) /
            100) *
          Number(taxPercent);
        reqObj.tax =
          Number(taxValue) *
          Number(typeof pre?.quantity === "undefined" ? 1 : pre?.quantity);
        reqObj.total =
          (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) *
            Number(typeof pre?.quantity === "undefined" ? 1 : pre?.quantity) ??
          0;
        if (typeof pre?.frequencyDuration === "string") {
          reqObj.frequencyDuration = pre?.frequencyDuration;
        }
        if (pre?.productId) reqObj.productId = pre?.productId;
        if (pre?.serviceId) reqObj.serviceId = pre?.serviceId;
        reqPres.push(reqObj);
      });
  }
  return {
    consumList: reqConsums ?? [],
    adminList: reqAdmins ?? [],
    prescriptions: reqPres ?? [],
  };
};

export const getProcedureDetails = (
  servId = null,
  varntId = null,
  serviceName = "",
  services = [],
  clinicStocks = [],
  pharmacyTaxId
) => {
  const consumAdmnList = getConsumAdminListByServIdAndVarntId(
    servId,
    varntId,
    services,
    clinicStocks,
    pharmacyTaxId
  );
  const procedureDetails = [];
  if (consumAdmnList?.consumList?.length > 0) {
    consumAdmnList?.consumList?.forEach((cl) => {
      const reqObj = {
        detailId: null,
        stockId: cl?.stockId ?? null,
        batchId: cl?.batchId ?? null,
        type: "Consumables",
        quantity: Number(cl?.quantity ?? 1),
        status: null,
        remarks: null,
        estimatedCost: cl?.estimatedCost ?? cl?.estCost,
        name: cl?.name,
        procedureName: serviceName,
        isDelete: false,
        serviceId: cl?.serviceId,
      };
      procedureDetails.push(reqObj);
    });
  }
  if (consumAdmnList?.adminList?.length > 0) {
    consumAdmnList?.adminList?.forEach((al) => {
      const reqObj = {
        detailId: null,
        stockId: al?.stockId ?? null,
        batchId: al?.batchId ?? null,
        type: "Administered",
        quantity: Number(al?.quantity ?? 1),
        status: null,
        remarks: null,
        estimatedCost: al?.estimatedCost ?? al?.estCost,
        name: al?.name,
        procedureName: serviceName,
        isDelete: false,
        serviceId: al?.serviceId,
        dosage: al?.dosage,
        route: al?.route?.value ?? null,
      };
      procedureDetails.push(reqObj);
    });
  }
  return {
    procedureDetails,
    prescriptions:
      consumAdmnList?.prescriptions?.length > 0
        ? consumAdmnList?.prescriptions
        : [],
  };
};

export function getDayOffsetNumber(dateStr) {
  const now = new Date();
  const target = new Date(dateStr);

  // Remove time for accurate day comparison
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = now - target; // positive if target is in the past
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays + 1;
}

export const getTimeSlotsBasedOnTasks = (list) => {
  const times = new Set();
  list?.forEach((item) => {
    const [startHour] = item?.startTime?.split(":").map(Number);

    if (item?.endTime) {
      const [endHour, endMinute] = item?.endTime?.split(":").map(Number);
      const finalEndHour = endMinute > 0 ? endHour + 1 : endHour;

      for (let h = startHour; h <= finalEndHour; h++) {
        times.add(String(h).padStart(2, "0") + ":00");
      }
    } else {
      times.add(String(startHour).padStart(2, "0") + ":00");
    }
  });
  return Array.from(times).sort();
};

export function getHourSlot(time) {
  const [hour] = time?.split(":")?.map(Number);
  return String(hour)?.padStart(2, "0") + ":00";
}

export const parseSoapNote = (noteString) => {
  try {
    // Clean the string first - remove extra spaces and ensure proper JSON format
    let cleanedString = noteString?.trim();

    // If it starts with a quote but no opening brace, add the opening brace
    if (cleanedString?.startsWith('"') && !cleanedString?.startsWith('{"')) {
      cleanedString = "{" + cleanedString;
    }

    // If it ends with a quote but no closing brace, add the closing brace
    if (cleanedString?.endsWith('"') && !cleanedString?.endsWith('"}')) {
      cleanedString = cleanedString + "}";
    }

    // Try to parse as JSON
    const jsonObject = JSON.parse(cleanedString);
    return jsonObject;
  } catch (error) {
    // If JSON parsing fails, try to fix common JSON issues
    try {
      let fixedString = noteString?.trim();

      // Remove leading/trailing quotes if they exist
      if (fixedString?.startsWith('"') && fixedString?.endsWith('"')) {
        fixedString = fixedString.slice(1, -1);
      }

      // Add braces if missing
      if (!fixedString.startsWith("{")) {
        fixedString = "{" + fixedString;
      }
      if (!fixedString.endsWith("}")) {
        fixedString = fixedString + "}";
      }

      const jsonObject = JSON.parse(fixedString);
      return jsonObject;
    } catch (secondError) {
      // If all JSON parsing fails, fall back to the original parsing logic
      const lines = noteString?.split("\n");
      const result = {};
      let currentKey = null;

      for (let line of lines) {
        line = line.trim();
        if (!line) continue;

        const keyMatch = line.match(/^([A-Za-z -]+):$/);
        if (keyMatch) {
          currentKey = keyMatch[1].replace(/-/g, "").replace(/\s+/g, "");
          result[currentKey] = "";
        } else if (currentKey) {
          if (result[currentKey]) {
            result[currentKey] += "\n" + line;
          } else {
            result[currentKey] = line;
          }
        }
      }

      return result;
    }
  }
};

export function filterRecordByHour(tasks, inputTime) {
  const inputHour = inputTime?.split(":")[0]; // e.g. "17"

  return tasks?.filter((task) => {
    if (!task?.startTime) return false;
    const taskHour = task?.startTime?.split(":")[0];
    return taskHour === inputHour; // match hour
  });
}

const billingUserAccess = [
  "BillingHistory",
  "Billing",
  "Payments",
  "PointOfSale",
  "PaymentMaster",
  "PaymentDashboard",
  "Returns",
];
const role = localStorage.getItem("role");

export const getAccessForScreen = (screen = "") => {
  if (role === "ROLE_CLINIC") {
    return true;
  }
  if (screen === "") return;
  if (role === "ROLE_BILLING") {
    return billingUserAccess?.includes(screen);
  }
};

export function getTimeDiffInMins(time1, time2) {
  if (!time1 || !time2) return 0; // if any time missing

  const [h1, m1] = time1.split(":").map(Number);
  const [h2, m2] = time2.split(":").map(Number);

  if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return 0; // invalid input

  // Convert to total minutes
  const minutes1 = h1 * 60 + m1;
  const minutes2 = h2 * 60 + m2;

  // Get absolute difference
  const diff = Math.abs(minutes1 - minutes2);

  // Convert back to hours and minutes
  const hours = Math.floor(diff / 60);
  const minutes = diff % 60;

  return { hours, minutes, totalMinutes: diff };
}

function flattenObject(obj) {
  return Object.entries(obj)
    ?.map(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        return `${key}: (${flattenObject(value)})`;
      }
      return `${key}: ${value}`;
    })
    ?.join(" | ");
}

export const getArryAsString = (arr) => {
  return arr?.length > 0
    ? arr?.map((doc) => flattenObject(doc)).join(" || ")
    : "";
};

export const getTodayDateMatchedRecords = (list = []) => {
  const formatDate = (dateStr) => new Date(dateStr).toISOString().split("T")[0];
  const result = list?.filter((li) => formatDate(li?.createdDate) === today);
  return result;
};

const KEYS = [
  "Summary",
  "Reminders",
  "InstructionsStatus",
  "AbnormalFindings",
  "KeyIssues",
  "RedFlags",
  "OtherUpdates",
  "Observation",
  "ProgressNotes",
  "Conclusion",
  "Protocols",
  "PreSurgicalDocumentation",
  "IntraSurgicalDocumentation",
  "PostSurgicalDocumentation",
  "OwnerCommunication",
  "AdditionalDocumentation",
];

export const processGroupedNotes = (notes = []) => {
  const grouped = notes?.reduce((result, note) => {
    const dateKey = note?.createdDate?.split("T")[0]; // YYYY-MM-DD only
    if (!result[dateKey]) result[dateKey] = [];
    result[dateKey].push(note);
    return result;
  }, {});

  // Consolidate & transform into string array
  return Object.values(grouped).flatMap((records) => {
    const consolidated = records?.find((r) => r?.consolidated);
    const finalRecords = consolidated ? [consolidated] : records;

    return finalRecords
      ?.map((note) => {
        const parts = [];
        KEYS.forEach((key) => {
          const reqKey = key.charAt(0).toLowerCase() + key.slice(1);
          if (note[reqKey]) {
            if (
              typeof note[reqKey] === "object" &&
              !Array.isArray(note[reqKey])
            ) {
              parts.push(`${key}: ${JSON.stringify(note[reqKey])}`);
            } else if (Array.isArray(note[reqKey])) {
              parts.push(`${key}: ${note[reqKey].join(", ")}`);
            } else {
              parts.push(`${key}: ${note[reqKey]}`);
            }
          }
        });
        return parts?.join(", ");
      })
      ?.filter(Boolean);
  });
};

export function toObject(data) {
  try {
    if (typeof data === "string") {
      return JSON.parse(data);
    }
    if (typeof data === "object" && data !== null) {
      return data;
    }
    throw new Error("Unsupported data type");
  } catch (err) {
    console.error("Error converting to object:", err);
    return null;
  }
}

export const transformData = (list = []) => {
  return list?.map((vnt) => {
    // Extract Consumables
    const consumables = (vnt?.consumList || [])
      ?.filter((c) => c?.delete === false)
      ?.map((c) => ({ name: c?.name, quantity: c?.quantity ?? 1 }));

    // Extract Administered
    const administered = (vnt?.presList || [])
      ?.filter((p) => p?.delete === false)
      ?.map((p) => ({
        name: p?.name,
        quantity: p?.quantity ?? 1,
        dosage: p?.dosage ?? "",
        route: p?.route?.value ?? null,
      }));

    // Extract prescriptions
    const prescriptions = (vnt?.prescriptions || [])
      ?.filter((pr) => pr?.delete === false)
      ?.map((pr) => ({
        name: pr?.name,
        quantity: pr?.quantity ?? 1,
        dosage: pr?.dosage ?? "",
        duration: pr?.duration,
        remarks: pr?.remarks,
        instructions: pr?.instructions?.value ?? null,
      }));

    // Build single string
    return `consumables:${JSON.stringify(
      consumables
    )}, administered:${JSON.stringify(
      administered
    )}, prescriptions:${JSON.stringify(prescriptions)}`;
  });
};

export const consolidateShouldBeTrue = (notes, type) => {
  const filtered = notes
    ?.filter((n) => n?.type === type) // filter by type
    ?.sort((a, b) => new Date(b?.createdDate) - new Date(a?.createdDate)); // sort desc
  if (!filtered?.length) return false;
  const latestConsolidated = filtered?.find((n) => n?.consolidated);
  // case: no consolidated → true only if at least 2 not consolidated exist
  if (!latestConsolidated) {
    return filtered?.filter((n) => !n?.consolidated)?.length >= 2;
  }
  // case: all consolidated → false
  if (filtered?.every((n) => n?.consolidated)) {
    return false;
  }
  // case: check if there is a !consolidated after latest consolidated
  const afterLatest = filtered?.filter(
    (n) =>
      !n?.consolidated &&
      new Date(n?.createdDate) > new Date(latestConsolidated?.createdDate)
  );
  if (afterLatest?.length) {
    // include older !consolidated also in counting
    const totalNotConsolidated = filtered?.filter(
      (n) => !n?.consolidated
    )?.length;
    return totalNotConsolidated >= 2;
  }
  return false;
};

export const consolidateShouldBeTrueForOutpatient = (
  notes = [],
  apntId = null
) => {
  const filtered = notes
    ?.filter((li) => li?.appointmentId === apntId)
    ?.sort((a, b) => new Date(b?.createdDate) - new Date(a?.createdDate)); // sort desc
  if (!filtered?.length) return false;
  const latestConsolidated = filtered?.find((n) => n?.consolidated);
  // case: no consolidated → true only if at least 2 not consolidated exist
  if (!latestConsolidated) {
    return filtered?.filter((n) => !n?.consolidated)?.length >= 2;
  }
  // case: all consolidated → false
  if (filtered?.every((n) => n?.consolidated)) {
    return false;
  }
  // case: check if there is a !consolidated after latest consolidated
  const afterLatest = filtered?.filter(
    (n) =>
      !n?.consolidated &&
      new Date(n?.createdDate) > new Date(latestConsolidated?.createdDate)
  );
  if (afterLatest?.length) {
    // include older !consolidated also in counting
    const totalNotConsolidated = filtered?.filter(
      (n) => !n?.consolidated
    )?.length;
    return totalNotConsolidated >= 2;
  }
  return false;
};

export const getPlanList = (planList = []) => {
  const result = [];

  Object.keys(planList)?.forEach((category) => {
    const items = planList?.[category];
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      const times = Number(item?.noOfTimes) || 1;

      if (item?.repeatTask) {
        // convert start & end times into minutes
        const [sH, sM] = item?.startTime?.split(":").map(Number);
        const [eH, eM] = item?.endTime?.split(":").map(Number);
        const startTotal = sH * 60 + sM;
        const endTotal = eH * 60 + eM;

        // total duration
        const totalDuration =
          endTotal >= startTotal
            ? endTotal - startTotal
            : 24 * 60 - startTotal + endTotal;

        // duration per slot
        const slotDuration = totalDuration / times;

        for (let i = 0; i < times; i++) {
          const currentStart = (startTotal + i * slotDuration) % (24 * 60);
          const currentEnd = (startTotal + (i + 1) * slotDuration) % (24 * 60);

          const formatTime = (mins) => {
            const h = String(Math.floor(mins / 60)).padStart(2, "0");
            const m = String(mins % 60).padStart(2, "0");
            return `${h}:${m}`;
          };

          // ✅ if startDate == endDate → keep same date
          // ✅ else → increment day when crossing midnight
          let dateStr = item?.startDate;
          let endDateStr = item?.endDate;

          if (item?.startDate !== item?.endDate) {
            // Handle multi-day case
            const tempDate = new Date(item?.startDate);
            tempDate.setDate(
              tempDate.getDate() +
                Math.floor((startTotal + i * slotDuration) / (24 * 60))
            );
            dateStr = tempDate.toISOString().split("T")[0];

            const tempEndDate = new Date(dateStr);
            tempEndDate.setDate(
              tempEndDate.getDate() + (currentEnd < currentStart ? 1 : 0) // if wrapped past midnight
            );
            endDateStr = tempEndDate.toISOString().split("T")[0];
          }

          result.push({
            category,
            task: item?.task,
            startDate: dateStr,
            startTime: formatTime(currentStart),
            repeatTask: item?.repeatTask,
            doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
            endDate: endDateStr,
            endTime: formatTime(currentEnd),
            times: item?.noOfTimes,
            stockId: item?.stockId ?? null,
            serviceId: item?.serviceId ?? null,
            variant:
              category?.toLowerCase() === "diagnostics"
                ? item?.variant ?? null
                : null,
          });
        }
      } else {
        // ✅ Handle non-repeat tasks (single entry)
        result.push({
          category,
          task: item?.task,
          startDate: item?.startDate,
          startTime: item?.startTime,
          repeatTask: item?.repeatTask,
          doctorId: item?.assignTo?.id ?? item?.assignTo?.value,
          endDate: item?.endDate,
          endTime: item?.endTime,
          times: item?.noOfTimes,
          stockId: item?.stockId ?? null,
          serviceId: item?.serviceId ?? null,
          variant:
            category?.toLowerCase() === "diagnostics"
              ? item?.variant ?? null
              : null,
        });
      }
    });
  });

  return result;
};
